/* =====================================================================
 * ChatModeContext — propagates the active workspace mode through the
 * chat component tree (PR-FE-1).
 *
 * Why this exists
 * ---------------
 * ``ChatArea`` / ``MessageInput`` / ``MessageBubble`` / ``AgentContent``
 * historically read feature flags with hard-coded ids like
 * ``useFeature('mode.agent.cloudAttach')``. That worked while the
 * unified agent was the only consumer. Doc-agent (PR-FE-2) reuses the
 * same chat components, so each gated capability has to resolve to
 * ``mode.<currentMode>.<name>`` instead of the fixed ``mode.agent.*``
 * prefix.
 *
 * The context carries one piece of state — the workspace mode — and
 * the :func:`useChatFeature` hook handles the namespacing so call
 * sites stay readable (``useChatFeature('cloudAttach')`` instead of
 * the longer ``useFeature(\`mode.${mode}.cloudAttach\`)`` dance).
 *
 * Defaults
 * --------
 * The default mode is ``'agent'`` so any caller that mounts
 * ``ChatArea`` without an explicit ``mode`` keeps the unified-agent
 * feature flag behaviour byte-identical with pre-PR-FE-1.
 * ===================================================================== */

import React from 'react';
import { useFeature } from '@auw/feature-flags';
import type { FeatureId } from '@auw/feature-flags';

/** Workspace modes the chat tree currently parameterises over. Keeping
 * this narrow on purpose — only modes that have a corresponding
 * ``mode.<X>.<chat-name>`` feature id family in
 * ``feature-flags/types.ts``. Add to the union as new feature
 * packages opt in. */
export type ChatMode = 'agent' | 'doc-agent';

/** Sub-feature names the chat tree gates on. Each name is suffixed
 * with the active :data:`ChatMode` to form the full feature id. The
 * union must stay in lock-step with the ``mode.agent.*`` and
 * ``mode.doc-agent.*`` ids declared in ``feature-flags/types.ts`` —
 * adding one without the other breaks the namespace lookup. */
export type ChatSubFeatureName =
  | 'cloudAttach'
  | 'mailAttach'
  | 'collabAttach'
  | 'messengerAttach'
  | 'skillUpload'
  | 'skillBrowse'
  | 'liveView'
  | 'outputDownload'
  | 'reactions'
  | 'groupChat';

const ChatModeContext = React.createContext<ChatMode>('agent');

/** Provide the active chat mode to descendants. ``ChatArea`` wraps its
 * subtree with this so :func:`useChatFeature` resolves the right
 * namespace; consumers outside ``ChatArea`` rarely need to call this
 * directly. */
export const ChatModeProvider = ChatModeContext.Provider;

/** Read the active chat mode (``'agent'`` by default). */
export function useChatMode(): ChatMode {
  return React.useContext(ChatModeContext);
}

/** Resolve a chat sub-feature flag against the current mode.
 *
 * ``useChatFeature('cloudAttach')`` →
 *   ``mode.agent.cloudAttach`` when mode is ``agent``
 *   ``mode.doc-agent.cloudAttach`` when mode is ``doc-agent``
 *
 * Undefined flag values resolve to ``false`` (the
 * ``FeatureFlagMap`` is a ``Partial<Record<FeatureId, boolean>>``),
 * so a tenant manifest that hasn't yet declared the doc-agent variant
 * simply has the capability hidden by default — exactly the
 * conservative posture PR-FE-3 will land before the chat-feature flags
 * become widely operator-tunable.
 */
export function useChatFeature(name: ChatSubFeatureName): boolean {
  const mode = useChatMode();
  // Cast: every (ChatMode × ChatSubFeatureName) pair is a registered
  // FeatureId per the type union in feature-flags/types.ts. If a future
  // ChatMode is added without its corresponding ids, TypeScript will
  // surface the mismatch at the type-union edit.
  const id = `mode.${mode}.${name}` as FeatureId;
  return useFeature(id);
}
